/**
 * useDailyRewardsViewModel
 *
 * ViewModel for the daily-rewards feature. Returns { state, actions, onMount }
 * per the strict MVVM contract (frontend expert skill).
 *
 * Server state lives exclusively in React Query (no Zustand).
 * Any banner-dismissed UI flag may live in Zustand (future story scope).
 *
 * Query keys (AC3):
 *   ['daily-rewards', 'me']            — personal daily standing
 *   ['daily-rewards', 'podium', day]   — podium for a closed day
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, skipToken } from '@tanstack/react-query';
import { dailyRewardsApi } from '../services/daily-rewards.api';
import type { ClaimableBonus, ClaimErrorState, LastResult, PodiumResponse } from '../types';
import { computeCountdownLabel } from '../lib/dailyCountdown';
import {
  isDayAcknowledged,
  acknowledgeDay as persistAcknowledgeDay
} from '../lib/acknowledgedDays';

// ---- Query key factory ----
export const dailyRewardsQueryKeys = {
  me: () => ['daily-rewards', 'me'] as const,
  podium: (day?: string) => ['daily-rewards', 'podium', day] as const
};

// ---- Internal helpers ----

interface ApiError extends Error {
  statusCode?: number;
  code?: string;
}

function mapClaimError(err: unknown): ClaimErrorState {
  if (err == null || typeof err !== 'object') return 'unknown';
  const e = err as ApiError;
  if (e.statusCode === 410 || e.code === 'BONUS_EXPIRED') return 'expired';
  if (e.code === 'ALREADY_CLAIMED') return 'already_claimed';
  if (e.code === 'REWARDS_DISABLED') return 'disabled';
  if (e.statusCode === 404 || e.code === 'NOTHING_TO_CLAIM') return 'not_found';
  return 'unknown';
}

// ---- ViewModel ----

/** Discriminant for the bottom-sheet top-content state (D→C→B→A precedence). */
export type SheetState = 'A' | 'B' | 'C' | 'D';

export interface DailyRewardsState {
  /** true while the /me query is loading */
  isMyDayLoading: boolean;
  /** Error from the /me query */
  myDayError: Error | null;
  /** Current GMT+3 day key (YYYY-MM-DD) — used to exclude today from the podium history */
  day: string | undefined;
  /** Pending claimable bonus (null when nothing to claim) */
  claimable: ClaimableBonus | null;
  /** Feature enabled flag (kill-switch) */
  isRewardsEnabled: boolean;
  /** Player's rank today (null when below eligibility minimum) */
  rank: number | null;
  /** Number of valid matches finished today */
  validMatches: number;
  /** Minimum valid matches required to be eligible for a bonus (the progress denominator) */
  eligibilityMin: number;
  /** Matches still needed to qualify for a bonus */
  matchesToQualify: number;
  /** Lazy-loaded podium data */
  podium: PodiumResponse | undefined;
  /** true while the podium query is loading */
  isPodiumLoading: boolean;
  /** Error from the claim mutation, mapped to a UI state */
  claimError: ClaimErrorState | null;
  /** true while the claim mutation is in flight */
  isClaiming: boolean;
  /** true after a successful claim in this session */
  claimSuccess: boolean;
  /** Amount credited by the last successful claim */
  claimedAmount: number | null;
  /**
   * Top-content state discriminant (D→C→B→A precedence):
   * D — claimable bonus pending
   * C — last closed day had no reward and not yet acknowledged
   * B — live rank on the leaderboard
   * A — start playing (default / no rank yet)
   */
  sheetState: SheetState;
  /**
   * Countdown to the GMT+3 day close ("XH YMIN").
   * Derived client-side — no backend field consumed.
   */
  countdownLabel: string;
  /** Last closed-day result from the API (may be absent). */
  lastResult: LastResult | null | undefined;
}

export interface DailyRewardsActions {
  /** Claim a pending bonus for the given day key */
  claim: (day: string) => Promise<void>;
  /** Trigger (or re-trigger) the podium query for the given day */
  loadPodium: (day?: string) => void;
  /** Reset the claim error state */
  clearClaimError: () => void;
  /**
   * Persist a client-only flag that the player has seen the "no reward" state
   * for the given closed day. Once set, sheetState will no longer return 'C'
   * for that day. Nothing is sent to the server.
   */
  acknowledgeDay: (day: string) => void;
}

export interface DailyRewardsViewModel {
  state: DailyRewardsState;
  actions: DailyRewardsActions;
  /** Call inside useEffect(onMount, []) on the consuming View */
  onMount: () => void;
}

export function useDailyRewardsViewModel(): DailyRewardsViewModel {
  const queryClient = useQueryClient();

  // ---- UI state for claim feedback (not server data — no Zustand needed) ----
  const [claimError, setClaimError] = useState<ClaimErrorState | null>(null);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [claimedAmount, setClaimedAmount] = useState<number | null>(null);

  // ---- Podium lazy-load control ----
  const [podiumDay, setPodiumDay] = useState<string | undefined>(undefined);
  const [podiumEnabled, setPodiumEnabled] = useState(false);

  // ---- Acknowledge version counter ----
  // Incrementing this forces a synchronous re-derivation of sheetState after
  // acknowledgeDay() is called, because isDayAcknowledged reads localStorage
  // and React won't re-render unless state changes.
  const [ackVersion, setAckVersion] = useState(0);

  // ---- Queries ----

  /**
   * ['daily-rewards', 'me'] — personal standing.
   * Enabled=false by default; onMount flips it to true.
   */
  const [myDayEnabled, setMyDayEnabled] = useState(false);

  const {
    data: myDay,
    status: myDayStatus,
    error: myDayError
  } = useQuery({
    queryKey: dailyRewardsQueryKeys.me(),
    queryFn: myDayEnabled ? () => dailyRewardsApi.getMyDay() : skipToken,
    staleTime: 60_000
  });

  const { data: podium, status: podiumStatus } = useQuery({
    queryKey: dailyRewardsQueryKeys.podium(podiumDay),
    queryFn: podiumEnabled ? () => dailyRewardsApi.getPodium({ day: podiumDay }) : skipToken,
    staleTime: 60_000
  });

  // ---- Claim mutation ----

  const claimMutation = useMutation({
    mutationFn: (day: string) => dailyRewardsApi.claim(day),
    onSuccess: (data) => {
      setClaimError(null);
      setClaimSuccess(true);
      setClaimedAmount(data.amount);
      // Invalidate balance (profile) and personal daily standing
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
      void queryClient.invalidateQueries({ queryKey: dailyRewardsQueryKeys.me() });
    },
    onError: (err: unknown) => {
      setClaimError(mapClaimError(err));
      setClaimSuccess(false);
    }
  });

  // ---- Derived state ----

  const isRewardsEnabled = myDay?.enabled ?? true;
  const claimable = myDay?.claimable ?? null;
  const rank = myDay?.rank ?? null;
  const validMatches = myDay?.validMatches ?? 0;
  const eligibilityMin = myDay?.eligibilityMin ?? 0;
  const matchesToQualify = myDay?.matchesToQualify ?? 0;
  const lastResult = myDay?.lastResult;

  // sheetState: D→C→B→A precedence (Story 01-01)
  // ackVersion is read to ensure re-derivation after acknowledgeDay().
  void ackVersion;
  const sheetState: SheetState = (() => {
    if (claimable != null) return 'D';
    if (lastResult != null && !lastResult.hasReward && !isDayAcknowledged(lastResult.day))
      return 'C';
    if (rank != null) return 'B';
    return 'A';
  })();

  // Countdown to GMT+3 day close — derived client-side, no backend field.
  const countdownLabel = myDay?.day ? computeCountdownLabel(myDay.day) : '0H 0MIN';

  // ---- Actions ----

  const actions: DailyRewardsActions = {
    claim: async (day: string) => {
      setClaimError(null);
      // mutateAsync rejects on error; onError handles error mapping into state.
      // Swallow the rejection here so Views don't need try/catch.
      await claimMutation.mutateAsync(day).catch(() => undefined);
    },
    loadPodium: (day?: string) => {
      setPodiumDay(day);
      setPodiumEnabled(true);
    },
    clearClaimError: () => {
      setClaimError(null);
    },
    acknowledgeDay: (day: string) => {
      persistAcknowledgeDay(day);
      // Bump counter so the sheetState derivation re-runs synchronously.
      setAckVersion((v) => v + 1);
    }
  };

  // ---- onMount ----

  const onMount = () => {
    setMyDayEnabled(true);
  };

  // ---- Assembled state ----

  const state: DailyRewardsState = {
    isMyDayLoading: myDayStatus === 'pending',
    myDayError: myDayError,
    day: myDay?.day,
    claimable,
    isRewardsEnabled,
    rank,
    validMatches,
    eligibilityMin,
    matchesToQualify,
    podium,
    // pending + enabled = actually fetching; pending + disabled (skipToken) = not yet requested
    isPodiumLoading: podiumEnabled && podiumStatus === 'pending',
    claimError,
    isClaiming: claimMutation.isPending,
    claimSuccess,
    claimedAmount,
    sheetState,
    countdownLabel,
    lastResult
  };

  return { state, actions, onMount };
}
